import React, {
	useRef,
	useEffect,
	useLayoutEffect,
	useState,
	useImperativeHandle,
} from "react";
import { gsap } from "gsap";

import {
	useEffectOnce,
	destroyVideo,
} from "../hooks/useEffectOnce";

import {
	isMobileOrTablet,
	isAndroid,
} from "../hooks/isMobile";

const CONTROL_REVEAL_DELAY = 300;
const CONTROL_FADE_DELAY = 1000;
const POST_SCRUB_MOVE_THRESHOLD = 8;

const sec2time = (timeInSeconds) => {
	var pad = function (
			num,
			size,
		) {
			return (
				"000" + num
			).slice(size * -1);
		},
		time =
			parseFloat(
				timeInSeconds,
			).toFixed(3),
		hours = Math.floor(
			time / 60 / 60,
		),
		minutes =
			Math.floor(
				time / 60,
			) % 60,
		seconds = Math.floor(
			time -
				minutes * 60,
		);

	return `${
		hours > 0
			? pad(hours, 2) +
			  ":"
			: ""
	}${pad(minutes, 2)}:${pad(
		seconds,
		2,
	)}`;
};

const clamp = (
	num,
	min,
	max,
) =>
	Math.min(
		Math.max(num, min),
		max,
	);

const VideoModal =
	React.forwardRef(
		(props, ref) => {
			const [
				refreshDate,
				setRefreshDate,
			] = useState(
				Date.now(),
			);

			const [
				vidSrc,
				setVidSrc,
			] =
				useState(
					undefined,
				);

			const [
				vidSubs,
				setVidSubs,
			] =
				useState(
					undefined,
				);

			const [open, setOpen] =
				useState(
					props.open ||
						false,
				);

			const [
				muted,
				setMuted,
			] =
				useState(false);

			const [
				subed,
				setSubed,
			] =
				useState(true);

			const [
				subsHaveBeenToogled,
				setSubsHaveBeenToogled,
			] =
				useState(false);

			const [
				playing,
				setPlaying,
			] =
				useState(false);

			const [
				timelineDragging,
				setTimelineDragging,
			] =
				useState(false);

			const [
				suppressCenterChrome,
				setSuppressCenterChrome,
			] =
				useState(false);

			const suppressCenterChromeRef =
				useRef(false);

			const openRef = useRef(
				props.open ||
					false,
			);

			const lastProgress =
				useRef(0);

			const vidRef =
				useRef(null);

			const vidControls =
				useRef(null);

			const timeElapsedTxt =
				useRef(null);

			const timeLeftTxt =
				useRef(null);

			const muteLogo =
				useRef(null);

			const subLogo =
				useRef(null);

			const playPauseIcon =
				useRef(null);

			const btnWrapper =
				useRef(null);

			const trackIndicator =
				useRef(null);

			const vidTrackProgressFill =
				useRef(null);

			const timelineDrag =
				useRef(false);

			const wasPlayingBeforeDrag =
				useRef(false);

			const cursorReleaseLock =
				useRef(false);

			const cursorReleaseRaf1 =
				useRef(null);

			const cursorReleaseRaf2 =
				useRef(null);

			const lastPointerPosition =
				useRef({
					x: 0,
					y: 0,
				});

			const postScrubMovementPending =
				useRef(false);

			const scrubReleasePoint =
				useRef({
					x: 0,
					y: 0,
				});

			const fadeOutTimeout =
				useRef(null);

			const initialCursorRaf =
				useRef(null);

			const controlsRevealAt =
				useRef(
					Infinity,
				);

			const controlsVisible =
				useRef(false);

			const progressTrack =
				useRef(null);

			const closeCallbackRef =
				useRef(null);

			const requestRef =
				useRef(null);

			const setCenterChromeSuppressed =
				(bool) => {
					suppressCenterChromeRef.current =
						bool;

					setSuppressCenterChrome(
						bool,
					);
				};

			const lockNativeScrubCursor =
				() => {
					document.documentElement.classList.add(
						"video-scrubbing",
					);
				};

			const unlockNativeScrubCursor =
				() => {
					document.documentElement.classList.remove(
						"video-scrubbing",
					);
				};

			const clearCursorReleaseRafs =
				() => {
					if (
						cursorReleaseRaf1.current
					) {
						cancelAnimationFrame(
							cursorReleaseRaf1.current,
						);

						cursorReleaseRaf1.current =
							null;
					}

					if (
						cursorReleaseRaf2.current
					) {
						cancelAnimationFrame(
							cursorReleaseRaf2.current,
						);

						cursorReleaseRaf2.current =
							null;
					}
				};

			const clearFadeoutTimeout =
				() => {
					if (
						fadeOutTimeout.current
					) {
						clearTimeout(
							fadeOutTimeout.current,
						);

						fadeOutTimeout.current =
							null;
					}
				};

			const clearInitialCursorRaf =
				() => {
					if (
						initialCursorRaf.current
					) {
						cancelAnimationFrame(
							initialCursorRaf.current,
						);

						initialCursorRaf.current =
							null;
					}
				};

			const shouldPinControls =
				() => {
					if (
						timelineDrag.current
					) {
						return true;
					}

					if (
						vidRef.current
							?.paused
					) {
						return true;
					}

					return false;
				};

			/*
			 * This drives BOTH:
			 *
			 * - internal video controls
			 * - Header X + ESC
			 */
			const handleControlVisiblity =
				(
					bool,
					instant = false,
				) => {
					const visibilityChanged =
						controlsVisible.current !==
						bool;

					controlsVisible.current =
						bool;

					if (
						visibilityChanged
					) {
						props.onChromeVisibilityChange?.(
							bool,
							instant,
						);
					}

					const moveTargets =
						[
							btnWrapper.current,
							playPauseIcon.current,
						].filter(
							Boolean,
						);

					if (
						vidControls.current
					) {
						gsap.killTweensOf(
							vidControls.current,
						);
					}

					gsap.killTweensOf(
						moveTargets,
					);

					if (bool) {
						if (
							instant
						) {
							if (
								vidControls.current
							) {
								gsap.set(
									vidControls.current,
									{
										autoAlpha: 1,
									},
								);
							}

							gsap.set(
								moveTargets,
								{
									yPercent: 0,
								},
							);
						} else {
							if (
								vidControls.current
							) {
								gsap.to(
									vidControls.current,
									0.5,
									{
										autoAlpha: 1,
									},
								);
							}

							gsap.to(
								moveTargets,
								0.3,
								{
									yPercent: 0,
								},
							);
						}
					} else {
						if (
							instant
						) {
							if (
								vidControls.current
							) {
								gsap.set(
									vidControls.current,
									{
										autoAlpha: 0,
									},
								);
							}

							gsap.set(
								moveTargets,
								{
									yPercent: 40,
								},
							);
						} else {
							if (
								vidControls.current
							) {
								gsap.to(
									vidControls.current,
									0.5,
									{
										autoAlpha: 0,
									},
								);
							}

							gsap.to(
								moveTargets,
								0.3,
								{
									yPercent: 40,
								},
							);
						}
					}
				};

			const showPinnedControls =
				() => {
					clearFadeoutTimeout();

					if (
						!controlsVisible.current
					) {
						handleControlVisiblity(
							true,
						);
					}

					if (
						timelineDrag.current ||
						cursorReleaseLock.current
					) {
						props.changeCursorState?.(
							"none",
						);

						return;
					}

					props.changeCursorState?.(
						"idle",
					);
				};

			const setFadeoutTimeout =
				() => {
					clearFadeoutTimeout();

					if (
						shouldPinControls()
					) {
						return;
					}

					if (
						cursorReleaseLock.current
					) {
						return;
					}

					fadeOutTimeout.current =
						setTimeout(
							() => {
								fadeOutTimeout.current =
									null;

								if (
									shouldPinControls() ||
									cursorReleaseLock.current
								) {
									showPinnedControls();

									return;
								}

								handleControlVisiblity(
									false,
								);

								props.changeCursorState?.(
									"hidden",
								);
							},
							CONTROL_FADE_DELAY,
						);
				};

			const requestFullscreen =
				() => {
					if (
						!document.fullscreenElement &&
						!document.mozFullScreenElement &&
						!document.webkitFullscreenElement &&
						!document.msFullscreenElement
					) {
						if (
							vidRef
								.current
								.requestFullscreen
						) {
							vidRef.current
								.requestFullscreen()
								.catch(
									() => {},
								);
						} else if (
							vidRef
								.current
								.msRequestFullscreen
						) {
							vidRef.current.msRequestFullscreen();
						} else if (
							vidRef
								.current
								.mozRequestFullScreen
						) {
							vidRef.current.mozRequestFullScreen();
						} else if (
							vidRef
								.current
								.webkitRequestFullscreen
						) {
							vidRef.current.webkitRequestFullscreen();
						}
					}
				};

			const handleTimeupdate =
				(
					automatic = false,
				) => {
					if (
						timelineDrag.current
					) {
						lastProgress.current =
							0;

						return;
					}

					if (
						!vidRef.current
					)
						return;

					const duration =
						vidRef
							.current
							.duration;

					const curTime =
						vidRef
							.current
							.currentTime;

					if (
						automatic &&
						lastProgress.current >
							curTime &&
						curTime -
							lastProgress.current >
							-1
					) {
						return;
					}

					if (!duration)
						return;

					requestAnimationFrame(
						() => {
							setProgress(
								curTime,
								duration,
							);
						},
					);

					lastProgress.current =
						curTime;
				};

			const handleVolumeChange =
				() => {
					if (
						!vidRef.current
					)
						return;

					const isMuted =
						vidRef
							.current
							?.muted ||
						false;

					console.log(
						isMuted,
					);
				};

			const toogleMuted = (
				e,
			) => {
				e.preventDefault();
				e.stopPropagation();

				if (!vidRef.current)
					return;

				vidRef.current.muted =
					!vidRef.current
						.muted;

				setMuted(
					vidRef.current
						.muted,
				);

				if (
					vidRef.current
						.muted &&
					!subsHaveBeenToogled
				) {
					showVideoSubtitles(
						true,
					);
				}
			};

			const showVideoSubtitles =
				(bool) => {
					setSubed(bool);

					const subExist =
						vidRef.current
							?.textTracks?.[0]
							?.mode ??
						false;

					if (!subExist)
						return;

					if (bool) {
						vidRef.current.textTracks[0].mode =
							"showing";
					} else {
						vidRef.current.textTracks[0].mode =
							"hidden";
					}
				};

			const toogleSubbed = (
				e,
			) => {
				e.preventDefault();
				e.stopPropagation();

				if (!vidRef.current)
					return;

				setSubsHaveBeenToogled(
					true,
				);

				const showSubsNext =
					!subed;

				showVideoSubtitles(
					showSubsNext,
				);
			};

			const setProgress = (
				curTime,
				duration,
			) => {
				const percent =
					duration === 0
						? 0
						: curTime /
						  duration;

				if (
					!vidTrackProgressFill.current
				)
					return;

				gsap.set(
					vidTrackProgressFill.current,
					{
						xPercent:
							-100 +
							percent *
								100,
					},
				);

				if (
					timeElapsedTxt.current
				) {
					timeElapsedTxt.current.innerText =
						sec2time(
							curTime,
						);
				}

				if (
					timeLeftTxt.current
				) {
					timeLeftTxt.current.innerText =
						"-" +
						sec2time(
							Math.max(
								0,
								duration -
									curTime,
							),
						);
				}
			};

			const animate = () => {
				handleTimeupdate(
					true,
				);

				requestRef.current =
					requestAnimationFrame(
						animate,
					);
			};

			const isFullscreen =
				() => {
					return (
						document.fullscreenElement !=
						null
					);
				};

			const handlePlay = () => {
				if (!vidRef.current)
					return;

				const playPromise =
					vidRef.current.play();

				if (
					playPromise !==
					undefined
				) {
					playPromise
						.then(
							() => {},
						)
						.catch(
							(
								error,
							) => {
								console.error(
									error,
								);
							},
						);
				}
			};

			const handlePause =
				() => {
					if (
						!vidRef.current
					)
						return;

					vidRef.current.pause();
				};

			const togglePlayPause =
				() => {
					if (
						!vidRef.current
					)
						return;

					if (
						timelineDrag.current ||
						cursorReleaseLock.current
					) {
						return;
					}

					if (
						vidRef
							.current
							.paused
					) {
						handlePlay();
					} else {
						handlePause();
					}
				};

			const requestClose =
				() => {
					props.onRequestClose?.();
				};

			const resetScrubState =
				() => {
					clearCursorReleaseRafs();

					timelineDrag.current =
						false;

					wasPlayingBeforeDrag.current =
						false;

					cursorReleaseLock.current =
						false;

					postScrubMovementPending.current =
						false;

					scrubReleasePoint.current =
						{
							x: 0,
							y: 0,
						};

					setTimelineDragging(
						false,
					);

					setCenterChromeSuppressed(
						false,
					);

					unlockNativeScrubCursor();
				};

			const handleExit = () => {
				clearFadeoutTimeout();
				clearInitialCursorRaf();

				openRef.current =
					false;

				resetScrubState();

				controlsRevealAt.current =
					Infinity;

				controlsVisible.current =
					false;

				props.onChromeVisibilityChange?.(
					false,
					true,
				);

				if (vidRef.current) {
					vidRef.current.style.display =
						"none";
				}

				closeCallbackRef.current?.();
			};

			const exitHandler = () => {
				if (
					document.exitFullscreen
				) {
					document.exitFullscreen();
				} else if (
					document.msExitFullscreen
				) {
					document.msExitFullscreen();
				} else if (
					document.mozCancelFullScreen
				) {
					document.mozCancelFullScreen();
				} else if (
					document.webkitExitFullscreen
				) {
					document.webkitExitFullscreen();
				}

				if (!isFullscreen()) {
					handleExit();
				}
			};

			const fullscreenchangeHandler =
				() => {
					if (
						!isFullscreen()
					) {
						handleExit();
					}
				};

			const handleResize = (
				e,
			) => {
				if (isAndroid) {
					fullscreenchangeHandler(
						e,
					);
				}
			};

			const handleOnPlay =
				() => {
					setPlaying(true);

					if (
						!openRef.current
					) {
						return;
					}

					if (
						suppressCenterChromeRef.current &&
						!timelineDrag.current &&
						!postScrubMovementPending.current
					) {
						setCenterChromeSuppressed(
							false,
						);
					}

					if (
						timelineDrag.current ||
						cursorReleaseLock.current
					) {
						return;
					}

					if (
						controlsVisible.current
					) {
						setFadeoutTimeout();
					}
				};

			const handleOnPause =
				() => {
					setPlaying(false);

					if (
						!openRef.current
					) {
						return;
					}

					if (
						timelineDrag.current ||
						cursorReleaseLock.current
					) {
						clearFadeoutTimeout();

						if (
							!controlsVisible.current
						) {
							handleControlVisiblity(
								true,
							);
						}

						props.changeCursorState?.(
							"none",
						);

						return;
					}

					showPinnedControls();
				};

			useImperativeHandle(
				ref,
				() => {
					return {
						open(
							bool,
							closeCallbackFn = null,
						) {
							openRef.current =
								bool;

							setOpen(
								bool,
							);

							if (
								bool
							) {
								clearFadeoutTimeout();
								clearInitialCursorRaf();

								resetScrubState();

								controlsRevealAt.current =
									Date.now() +
									CONTROL_REVEAL_DELAY;

								controlsVisible.current =
									false;

								/*
								 * X + ESC are hidden immediately but remain
								 * mounted in Header.
								 */
								props.onChromeVisibilityChange?.(
									false,
									true,
								);

								handleControlVisiblity(
									false,
									true,
								);

								props.changeCursorState?.(
									"hidden",
								);

								if (
									vidRef.current
								) {
									vidRef.current.style.display =
										"inherit";

									vidRef.current.currentTime =
										0;

									vidRef.current.muted =
										muted;

									if (
										!subsHaveBeenToogled &&
										muted
									) {
										showVideoSubtitles(
											true,
										);
									} else {
										showVideoSubtitles(
											subed,
										);
									}

									setProgress(
										0,
										0,
									);
								}

								handlePlay();

								lastProgress.current =
									0;

								if (
									closeCallbackFn
								) {
									closeCallbackRef.current =
										closeCallbackFn;
								} else {
									closeCallbackRef.current =
										null;
								}

								if (
									isMobileOrTablet
								) {
									requestFullscreen();
								}
							} else {
								clearFadeoutTimeout();
								clearInitialCursorRaf();

								resetScrubState();

								controlsRevealAt.current =
									Infinity;

								controlsVisible.current =
									false;

								props.onChromeVisibilityChange?.(
									false,
									true,
								);

								handleControlVisiblity(
									false,
									true,
								);

								props.changeCursorState?.(
									"idle",
								);

								handlePause();

								closeCallbackRef.current =
									null;

								lastProgress.current =
									0;

								if (
									vidRef.current
								) {
									vidRef.current.style.display =
										"none";

									requestAnimationFrame(
										() => {
											if (
												!vidRef.current
											) {
												return;
											}

											vidRef.current.currentTime =
												0;

											if (
												isMobileOrTablet
											) {
												setRefreshDate(
													Date.now(),
												);
											}
										},
									);
								}
							}
						},
					};
				},
				[open],
			);

			useLayoutEffect(
				() => {
					if (
						!open ||
						isMobileOrTablet
					) {
						return;
					}

					clearInitialCursorRaf();

					props.changeCursorState?.(
						"hidden",
					);

					initialCursorRaf.current =
						requestAnimationFrame(
							() => {
								initialCursorRaf.current =
									null;

								props.changeCursorState?.(
									"hidden",
								);
							},
						);

					return () => {
						clearInitialCursorRaf();
					};
				},
				[open],
			);

			/*
			 * SPACE only.
			 *
			 * Escape is intentionally handled by Header.jsx so
			 * keyboard Escape and clicking the X use the exact
			 * same callback and cannot double-fire.
			 */
			useEffect(() => {
				if (!open)
					return;

				const handleKeyDown =
					(e) => {
						const target =
							e.target;

						const tagName =
							target?.tagName?.toLowerCase?.();

						const isEditable =
							tagName ===
								"input" ||
							tagName ===
								"textarea" ||
							tagName ===
								"select" ||
							target?.isContentEditable;

						if (
							isEditable
						) {
							return;
						}

						if (
							e.code ===
								"Space" ||
							e.key === " "
						) {
							if (
								e.repeat
							) {
								return;
							}

							e.preventDefault();
							e.stopPropagation();

							togglePlayPause();
						}
					};

				window.addEventListener(
					"keydown",
					handleKeyDown,
					true,
				);

				return () => {
					window.removeEventListener(
						"keydown",
						handleKeyDown,
						true,
					);
				};
			}, [open]);

			useEffectOnce(() => {
				const vidEl =
					vidRef.current;

				return () => {
					clearFadeoutTimeout();
					clearInitialCursorRaf();
					clearCursorReleaseRafs();

					unlockNativeScrubCursor();

					destroyVideo(
						vidEl,
					);
				};
			});

			useEffect(() => {
				const _vidRefCurrent =
					vidRef.current;

				const handleEnded =
					() => {
						if (
							!isMobileOrTablet
						) {
							requestClose();
						}
					};

				if (open) {
					requestRef.current =
						requestAnimationFrame(
							animate,
						);
				}

				if (
					_vidRefCurrent
				) {
					_vidRefCurrent.addEventListener(
						"volumechange",
						handleVolumeChange,
					);

					_vidRefCurrent.addEventListener(
						"play",
						handleOnPlay,
					);

					_vidRefCurrent.addEventListener(
						"pause",
						handleOnPause,
					);

					_vidRefCurrent.addEventListener(
						"ended",
						handleEnded,
					);

					_vidRefCurrent.addEventListener(
						"webkitendfullscreen",
						exitHandler,
						false,
					);

					_vidRefCurrent.addEventListener(
						"fullscreenchange",
						fullscreenchangeHandler,
						false,
					);

					_vidRefCurrent.addEventListener(
						"mozfullscreenchange",
						fullscreenchangeHandler,
						false,
					);

					_vidRefCurrent.addEventListener(
						"MSFullscreenChange",
						fullscreenchangeHandler,
						false,
					);

					_vidRefCurrent.addEventListener(
						"webkitfullscreenchange",
						fullscreenchangeHandler,
						false,
					);
				}

				window.addEventListener(
					"resize",
					handleResize,
				);

				return () => {
					cancelAnimationFrame(
						requestRef.current,
					);

					window.removeEventListener(
						"resize",
						handleResize,
					);

					if (
						_vidRefCurrent
					) {
						_vidRefCurrent.removeEventListener(
							"volumechange",
							handleVolumeChange,
						);

						_vidRefCurrent.removeEventListener(
							"play",
							handleOnPlay,
						);

						_vidRefCurrent.removeEventListener(
							"pause",
							handleOnPause,
						);

						_vidRefCurrent.removeEventListener(
							"ended",
							handleEnded,
						);

						_vidRefCurrent.removeEventListener(
							"webkitendfullscreen",
							exitHandler,
							false,
						);

						_vidRefCurrent.removeEventListener(
							"fullscreenchange",
							fullscreenchangeHandler,
							false,
						);

						_vidRefCurrent.removeEventListener(
							"mozfullscreenchange",
							fullscreenchangeHandler,
							false,
						);

						_vidRefCurrent.removeEventListener(
							"MSFullscreenChange",
							fullscreenchangeHandler,
							false,
						);

						_vidRefCurrent.removeEventListener(
							"webkitfullscreenchange",
							fullscreenchangeHandler,
							false,
						);
					}
				};
			}, [open]);

			useEffect(() => {
				if (
					props.src !==
					vidSrc
				) {
					setVidSrc(
						props.src,
					);
				}
			}, [props.src]);

			useEffect(() => {
				if (
					props.subs !==
					vidSubs
				) {
					setVidSubs(
						props.subs,
					);
				}
			}, [props.subs]);

			const resolveCursorAfterScrub =
				(
					clientX,
					clientY,
				) => {
					clearCursorReleaseRafs();

					cursorReleaseLock.current =
						true;

					lockNativeScrubCursor();

					props.changeCursorState?.(
						"none",
					);

					cursorReleaseRaf1.current =
						requestAnimationFrame(
							() => {
								cursorReleaseRaf1.current =
									null;

								cursorReleaseRaf2.current =
									requestAnimationFrame(
										() => {
											cursorReleaseRaf2.current =
												null;

											cursorReleaseLock.current =
												false;

											unlockNativeScrubCursor();

											const x =
												typeof clientX ===
												"number"
													? clientX
													: lastPointerPosition
															.current
															.x;

											const y =
												typeof clientY ===
												"number"
													? clientY
													: lastPointerPosition
															.current
															.y;

											const elementUnderPointer =
												document.elementFromPoint(
													x,
													y,
												);

											const isOverProgress =
												elementUnderPointer instanceof
													Element &&
												elementUnderPointer.closest(
													".vid-progress-cnt",
												);

											if (
												isOverProgress
											) {
												props.changeCursorState?.(
													"none",
												);
											} else if (
												controlsVisible.current
											) {
												props.changeCursorState?.(
													"idle",
												);
											} else {
												props.changeCursorState?.(
													"hidden",
												);
											}

											if (
												vidRef.current &&
												!vidRef
													.current
													.paused &&
												controlsVisible.current
											) {
												setFadeoutTimeout();
											}
										},
									);
							},
						);
				};

			useEffect(() => {
				if (
					!open ||
					isMobileOrTablet
				) {
					return;
				}

				const handleMouseActivity =
					(e) => {
						lastPointerPosition.current =
							{
								x:
									e.clientX,
								y:
									e.clientY,
							};

						if (
							timelineDrag.current ||
							cursorReleaseLock.current
						) {
							clearFadeoutTimeout();

							if (
								timelineDrag.current &&
								!controlsVisible.current
							) {
								handleControlVisiblity(
									true,
								);
							}

							props.changeCursorState?.(
								"none",
							);

							return;
						}

						if (
							postScrubMovementPending.current
						) {
							const dx =
								e.clientX -
								scrubReleasePoint
									.current
									.x;

							const dy =
								e.clientY -
								scrubReleasePoint
									.current
									.y;

							const distance =
								Math.sqrt(
									dx *
										dx +
										dy *
											dy,
								);

							if (
								distance <
								POST_SCRUB_MOVE_THRESHOLD
							) {
								return;
							}

							postScrubMovementPending.current =
								false;

							setCenterChromeSuppressed(
								false,
							);
						}

						if (
							Date.now() <
							controlsRevealAt.current
						) {
							props.changeCursorState?.(
								"hidden",
							);

							return;
						}

						if (
							!controlsVisible.current
						) {
							handleControlVisiblity(
								true,
							);
						}

						const isOverProgress =
							e.target instanceof
								Element &&
							e.target.closest(
								".vid-progress-cnt",
							);

						if (
							isOverProgress
						) {
							props.changeCursorState?.(
								"none",
							);
						} else {
							props.changeCursorState?.(
								"idle",
							);
						}

						setFadeoutTimeout();
					};

				const handleGlobalMouseUp =
					(e) => {
						if (
							!timelineDrag.current
						) {
							return;
						}

						handleTouchEnd(
							e,
						);
					};

				window.addEventListener(
					"mousemove",
					handleMouseActivity,
					true,
				);

				window.addEventListener(
					"mouseup",
					handleGlobalMouseUp,
					true,
				);

				return () => {
					window.removeEventListener(
						"mousemove",
						handleMouseActivity,
						true,
					);

					window.removeEventListener(
						"mouseup",
						handleGlobalMouseUp,
						true,
					);
				};
			}, [open]);

			const handleHover = (
				e,
				bool,
			) => {
				if (!e.target)
					return;

				const hasHover =
					e.target.classList.contains(
						"hover",
					);

				if (
					bool &&
					!hasHover
				) {
					e.target.classList.add(
						"hover",
					);
				} else if (
					!bool &&
					hasHover
				) {
					e.target.classList.remove(
						"hover",
					);
				}
			};

			const enterFullScreen = (
				e,
			) => {
				e.preventDefault();
				e.stopPropagation();

				if (!vidRef.current)
					return;

				if (
					vidRef.current
						.requestFullscreen
				) {
					vidRef.current.requestFullscreen();
				} else if (
					vidRef.current
						.webkitRequestFullscreen
				) {
					vidRef.current.webkitRequestFullscreen();
				} else if (
					vidRef.current
						.msRequestFullScreen
				) {
					vidRef.current.msRequestFullScreen();
				}
			};

			const tooglePlayPause =
				(e) => {
					e.stopPropagation();

					if (
						!vidRef.current
					)
						return;

					if (
						timelineDrag.current ||
						cursorReleaseLock.current
					) {
						return;
					}

					if (
						e.target.classList.contains(
							"vid-progress-cnt",
						)
					) {
						return;
					}

					if (
						isFullscreen()
					) {
						return;
					}

					togglePlayPause();
				};

			const updateIndicator =
				(
					e,
					visible,
				) => {
					if (visible) {
						const rect =
							e.target.getBoundingClientRect();

						const x =
							e.clientX -
							rect.left;

						gsap.to(
							trackIndicator.current,
							0,
							{
								autoAlpha: 1,
								x,
							},
						);
					} else {
						gsap.to(
							trackIndicator.current,
							0.3,
							{
								autoAlpha: 0,
							},
						);
					}
				};

			const updateTimeFromDrag =
				(e) => {
					const vid =
						vidRef.current;

					if (!vid)
						return;

					const duration =
						vid.duration;

					if (!duration)
						return;

					const rect =
						progressTrack.current?.getBoundingClientRect();

					if (!rect)
						return;

					const width =
						rect.width;

					const x =
						e.clientX -
						rect.left;

					const progress =
						clamp(
							x /
								width,
							0,
							1,
						);

					const curTime =
						progress *
						duration;

					vid.currentTime =
						curTime;

					setProgress(
						curTime,
						duration,
					);
				};

			const handleTouchStart =
				(e) => {
					e.preventDefault();
					e.stopPropagation();

					const vid =
						vidRef.current;

					if (!vid)
						return;

					if (
						timelineDrag.current
					) {
						return;
					}

					clearCursorReleaseRafs();

					cursorReleaseLock.current =
						false;

					postScrubMovementPending.current =
						false;

					wasPlayingBeforeDrag.current =
						!vid.paused;

					timelineDrag.current =
						true;

					setTimelineDragging(
						true,
					);

					setCenterChromeSuppressed(
						true,
					);

					lockNativeScrubCursor();

					clearFadeoutTimeout();

					if (
						!controlsVisible.current
					) {
						handleControlVisiblity(
							true,
						);
					}

					props.changeCursorState?.(
						"none",
					);

					if (!vid.paused) {
						vid.pause();
					}

					updateTimeFromDrag(
						e,
					);
				};

			const handleTouchEnd =
				(e) => {
					if (
						!timelineDrag.current
					) {
						return;
					}

					if (
						e?.cancelable
					) {
						e.preventDefault();
					}

					e?.stopPropagation?.();

					const clientX =
						typeof e?.clientX ===
						"number"
							? e.clientX
							: lastPointerPosition
									.current
									.x;

					const clientY =
						typeof e?.clientY ===
						"number"
							? e.clientY
							: lastPointerPosition
									.current
									.y;

					lastPointerPosition.current =
						{
							x: clientX,
							y: clientY,
						};

					scrubReleasePoint.current =
						{
							x: clientX,
							y: clientY,
						};

					postScrubMovementPending.current =
						true;

					const shouldResume =
						wasPlayingBeforeDrag.current;

					wasPlayingBeforeDrag.current =
						false;

					timelineDrag.current =
						false;

					setTimelineDragging(
						false,
					);

					setCenterChromeSuppressed(
						true,
					);

					resolveCursorAfterScrub(
						clientX,
						clientY,
					);

					if (
						shouldResume
					) {
						handlePlay();
					} else {
						clearFadeoutTimeout();

						if (
							!controlsVisible.current
						) {
							handleControlVisiblity(
								true,
							);
						}
					}
				};

			const preventDefault =
				(e) => {
					e.preventDefault();
					e.stopPropagation();
				};

			const handleMouseMoveModal =
				(e) => {
					lastPointerPosition.current =
						{
							x:
								e.clientX,
							y:
								e.clientY,
						};

					if (
						timelineDrag.current &&
						progressTrack.current
					) {
						const vid =
							vidRef.current;

						if (!vid)
							return;

						const duration =
							vid.duration;

						if (!duration)
							return;

						const rect =
							progressTrack.current.getBoundingClientRect();

						const width =
							rect.width;

						const x =
							e.clientX -
							rect.left;

						const progress =
							clamp(
								x /
									width,
								0,
								1,
							);

						const curTime =
							progress *
							duration;

						vid.currentTime =
							curTime;

						setProgress(
							curTime,
							duration,
						);

						props.changeCursorState?.(
							"none",
						);
					}
				};

			return (
				<>
					<style>
						{`
							html.video-scrubbing,
							html.video-scrubbing body,
							html.video-scrubbing body *,
							html.video-scrubbing body *::before,
							html.video-scrubbing body *::after {
								cursor: none !important;
							}

							.video-btn-wrapper.suppress-center-chrome {
								background: transparent !important;
							}

							.video-btn-wrapper.suppress-center-chrome::before,
							.video-btn-wrapper.suppress-center-chrome::after {
								background: transparent !important;
								opacity: 0 !important;
								visibility: hidden !important;
							}

							.video-btn-wrapper.suppress-center-chrome .vid-btn-play {
								opacity: 0 !important;
								visibility: hidden !important;
							}
						`}
					</style>

					<div
						className="video-modal"
						style={{
							zIndex: 9,

							display:
								open
									? "flex"
									: "none",

							transform:
								"translateZ(0)",

							cursor:
								"none",
						}}
						onMouseMove={
							handleMouseMoveModal
						}
					>
						<div
							className="video-content"
							onMouseLeave={() => {
								if (
									timelineDrag.current ||
									cursorReleaseLock.current
								) {
									props.changeCursorState?.(
										"none",
									);

									return;
								}

								if (
									shouldPinControls()
								) {
									return;
								}

								if (
									!controlsVisible.current
								) {
									return;
								}

								handleControlVisiblity(
									false,
								);

								props.changeCursorState?.(
									"hidden",
								);
							}}
							onMouseDown={
								tooglePlayPause
							}
						>
							<video
								key={
									vidSrc +
									"" +
									refreshDate
								}
								ref={
									vidRef
								}
								preload="none"
								className="modalVid"
								type={
									"video/mp4"
								}
								style={{
									width:
										"100%",

									height:
										"100%",

									maxHeight:
										"100vh",

									maxWidth:
										"100vw",

									minHeight:
										"100%",

									minWidth:
										"100%",

									opacity:
										1,

									cursor:
										"none",
								}}
								src={
									vidSrc
								}
							>
								{
									props.subtrack
								}
							</video>

							<div
								className={`video-btn-wrapper${
									timelineDragging
										? " dragging"
										: ""
								}${
									suppressCenterChrome
										? " suppress-center-chrome"
										: ""
								}`}
								style={{
									transform:
										"translateZ(0)",

									display:
										isMobileOrTablet
											? "none"
											: "flex",
								}}
								ref={
									vidControls
								}
							>
								<div
									className="vid-btn-loading"
									style={{
										display:
											"none",
									}}
								></div>

								<div
									className={`vid-btn-play ${
										!playing
											? "play"
											: "pause"
									}`}
									ref={
										playPauseIcon
									}
								></div>

								<div
									className="vid-lower-btn-wrapper"
									ref={
										btnWrapper
									}
								>
									<div className="vid-main-controls">
										<div
											className="vid-main-btn-wrapper"
											onClick={
												toogleMuted
											}
											onMouseDown={
												preventDefault
											}
											onMouseEnter={(
												e,
											) => {
												handleHover(
													e,
													true,
												);
											}}
											onMouseLeave={(
												e,
											) => {
												handleHover(
													e,
													false,
												);
											}}
											style={{
												cursor:
													"none",
											}}
										>
											<div
												style={{
													pointerEvents:
														"none",
												}}
												className={`vid-main-button${
													muted
														? " mute"
														: " mute-off"
												}`}
												ref={
													muteLogo
												}
											></div>
										</div>

										{props.subtrack ? (
											<div
												className="vid-main-btn-wrapper"
												onClick={
													toogleSubbed
												}
												onMouseDown={
													preventDefault
												}
												onMouseEnter={(
													e,
												) => {
													handleHover(
														e,
														true,
													);
												}}
												onMouseLeave={(
													e,
												) => {
													handleHover(
														e,
														false,
													);
												}}
												style={{
													cursor:
														"none",
												}}
											>
												<div
													style={{
														pointerEvents:
															"none",
													}}
													className={`vid-main-button${
														subed
															? " subtitle-on"
															: " subtitle-off"
													}`}
													ref={
														subLogo
													}
												></div>
											</div>
										) : null}

										<div
											className="vid-main-control-progress-cnt"
											style={{
												pointerEvents:
													"none",
											}}
										>
											<div
												className="controls-elapsed-time-indicator"
												style={{
													pointerEvents:
														"none",
												}}
											>
												<div
													className="elapsed-time-txt"
													ref={
														timeElapsedTxt
													}
												>
													02:21
												</div>
											</div>

											<div
												ref={
													progressTrack
												}
												className="vid-progress-cnt"
												onMouseDown={(
													e,
												) => {
													handleTouchStart(
														e,
													);
												}}
												onMouseEnter={(
													e,
												) => {
													updateIndicator(
														e,
														true,
													);

													props.changeCursorState?.(
														"none",
													);
												}}
												onMouseLeave={(
													e,
												) => {
													updateIndicator(
														e,
														false,
													);

													if (
														timelineDrag.current ||
														cursorReleaseLock.current
													) {
														props.changeCursorState?.(
															"none",
														);

														return;
													}

													if (
														Date.now() >=
														controlsRevealAt.current
													) {
														props.changeCursorState?.(
															"idle",
														);
													}
												}}
												onMouseUp={(
													e,
												) => {
													updateIndicator(
														e,
														false,
													);

													handleTouchEnd(
														e,
													);
												}}
												onClick={
													preventDefault
												}
												style={{
													pointerEvents:
														"auto",

													cursor:
														"none",
												}}
												onMouseMove={(
													e,
												) => {
													updateIndicator(
														e,
														true,
													);

													if (
														timelineDrag.current ||
														cursorReleaseLock.current
													) {
														props.changeCursorState?.(
															"none",
														);
													}
												}}
											>
												<div
													className="vid-progress-track"
													style={{
														pointerEvents:
															"none",

														transform:
															"translateZ(0)",
													}}
												>
													<div
														className="vid-progress-fill"
														ref={
															vidTrackProgressFill
														}
														style={{
															pointerEvents:
																"none",
														}}
													></div>

													<div
														className="vid-progress-hover-indicator inactive"
														ref={
															trackIndicator
														}
														style={{
															pointerEvents:
																"none",
														}}
													></div>
												</div>
											</div>

											<div className="controls-elapsed-time-indicator">
												<div
													className="elapsed-time-txt"
													ref={
														timeLeftTxt
													}
												>
													-02:01
												</div>
											</div>
										</div>

										<div
											className="vid-main-btn-wrapper"
											onClick={
												enterFullScreen
											}
											onMouseDown={
												preventDefault
											}
											onMouseEnter={(
												e,
											) => {
												handleHover(
													e,
													true,
												);
											}}
											onMouseLeave={(
												e,
											) => {
												handleHover(
													e,
													false,
												);
											}}
											style={{
												cursor:
													"none",
											}}
										>
											<div
												className="vid-main-button fullscreen"
												style={{
													pointerEvents:
														"none",
												}}
											></div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</>
			);
		},
	);

export default VideoModal;